import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaHistory, FaDownload, FaSignInAlt, FaSignOutAlt, FaClock, FaSearch, FaEye, FaTimes, FaInfoCircle } from 'react-icons/fa';
import { FiAlertTriangle, FiSunrise, FiSunset, FiArrowDownCircle, FiCoffee } from 'react-icons/fi';
import { FaCalendarAlt } from 'react-icons/fa';
import useAxios from '../utils/useAxios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useSidebar } from '../context/SidebarContext';
import styles from '../styles/PresencePage';
import OrganisationSelector from '../components/OrganisationSelector';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const PresencePage = () => {
  const [presences, setPresences] = useState([]);
  const [pagination, setPagination] = useState({ totalPages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [periodeFilter, setPeriodeFilter] = useState('');
  const [statutFilter, setStatutFilter] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const axiosInstance = useAxios();
  const { sidebarVisible } = useSidebar();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [selectedImage, setSelectedImage] = useState(null);
  const [motifModal, setMotifModal] = useState({ show: false, name: '', motif: '' });
  const [selectedOrganisation, setSelectedOrganisation] = useState('');
  const [currentOrganisation, setCurrentOrganisation] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const [hasExceptionnelleToday, setHasExceptionnelleToday] = useState(false);
  const [showExceptionnelleBanner, setShowExceptionnelleBanner] = useState(false);
  const [loadingExceptionnelle, setLoadingExceptionnelle] = useState(false);

  // Modal justification (colonne Justifié du tableau)
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailJustif, setDetailJustif] = useState(null);
  const closeDetailModal = () => { setShowDetailModal(false); setDetailJustif(null); };

  // États absences approuvées
  const [absencesApprouvees, setAbsencesApprouvees] = useState({ count: 0, employes: [] });
  const [showAbsencesBanner, setShowAbsencesBanner] = useState(false);
  const [showAbsencesListModal, setShowAbsencesListModal] = useState(false);

  // Modal détail d'une absence approuvée (ouvert depuis la liste)
  const [showDetailAbsenceModal, setShowDetailAbsenceModal] = useState(false);
  const [detailAbsenceApprouvee, setDetailAbsenceApprouvee] = useState(null);
  const closeDetailAbsenceModal = () => { setShowDetailAbsenceModal(false); setDetailAbsenceApprouvee(null); };

  const handleOrganisationChange = (orgId, org) => {
    setSelectedOrganisation(orgId);
    setCurrentOrganisation(org);
    fetchPresences(1, orgId);
  };

  const fetchPresences = async (targetPage = 1, orgId = selectedOrganisation) => {
    if (!orgId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: targetPage,
        search,
        periode: periodeFilter,
        statut: statutFilter,
        date: selectedDate,
        organisation: orgId,
        page_size: pageSize === 'all' ? totalCount : pageSize
      });
      const response = await axiosInstance.get(`/pointage/presences-today/?${params}`);
      setPresences(response.data.results);
      setTotalCount(response.data.count);
      setPagination({
        totalPages: pageSize === 'all' ? 1 : Math.ceil(response.data.count / pageSize)
      });
      setPage(targetPage);
    } catch (err) {
      console.error("Erreur lors de la récupération des présences", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAbsencesApprouvees = async (orgId = selectedOrganisation, date = selectedDate) => {
    if (!orgId || !date) return;
    try {
      const params = new URLSearchParams({ date, organisation: orgId });
      const response = await axiosInstance.get(`/pointage/absences-approuvees-today/?${params}`);
      setAbsencesApprouvees(response.data);
      setShowAbsencesBanner(response.data.count > 0);
    } catch (err) {
      console.error("Erreur absences approuvées", err);
      setAbsencesApprouvees({ count: 0, employes: [] });
      setShowAbsencesBanner(false);
    }
  };

  const checkExceptionnellePresences = async () => {
    if (!selectedOrganisation || !selectedDate) return;
    try {
      const params = new URLSearchParams({
        date: selectedDate,
        organisation: selectedOrganisation,
        page_size: 1
      });
      const response = await axiosInstance.get(`/pointage/presence-exceptionnelle/?${params}`);
      const hasPresences = response.data.count > 0;
      setHasExceptionnelleToday(hasPresences);
      setShowExceptionnelleBanner(hasPresences);
    } catch (err) {
      setHasExceptionnelleToday(false);
      setShowExceptionnelleBanner(false);
    }
  };

  const loadExceptionnellePresences = async () => {
    if (!selectedOrganisation || !selectedDate) return;
    setLoadingExceptionnelle(true);
    try {
      navigate(`/presence-exeptionnelle?organisation=${selectedOrganisation}&date=${selectedDate}`);
    } catch (err) {
      console.error("Erreur lors de la redirection", err);
    } finally {
      setLoadingExceptionnelle(false);
    }
  };

  useEffect(() => {
    if (selectedOrganisation) {
      fetchPresences(1);
      fetchAbsencesApprouvees();
    }
  }, [search, periodeFilter, statutFilter, pageSize, selectedDate, selectedOrganisation]);

  useEffect(() => {
    if (selectedOrganisation && selectedDate) {
      checkExceptionnellePresences();
    }
  }, [selectedDate, selectedOrganisation]);

  const openImageModal = (presence) => setSelectedImage({
    src: presence.photo,
    name: `${presence.employe_prenom} ${presence.employe_nom}`
  });
  const closeImageModal = () => setSelectedImage(null);

  const openMotifModal = (presence) => setMotifModal({
    show: true,
    name: `${presence.employe_prenom} ${presence.employe_nom}`,
    motif: presence.motif_sortie,
  });
  const closeMotifModal = () => setMotifModal({ show: false, name: '', motif: '' });

  const generatePageSizeOptions = () => {
    const options = [10, 20, 30, 50];
    if (totalCount > 50 && !options.includes(totalCount)) options.push(totalCount);
    options.push('all');
    return options;
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) fetchPresences(newPage);
  };

  const formatHeure = (heure) => (heure ? heure.split('.')[0] : '-');

  const fetchAllPresencesForToday = async () => {
    try {
      let allPresences = [];
      let currentPage = 1;
      let hasMore = true;
      while (hasMore) {
        const params = new URLSearchParams({
          page: currentPage, search, periode: periodeFilter, statut: statutFilter,
          date: selectedDate, organisation: selectedOrganisation, page_size: 50
        });
        const response = await axiosInstance.get(`/pointage/presences-today/?${params}`);
        allPresences = [...allPresences, ...response.data.results];
        hasMore = currentPage * 50 < response.data.count;
        currentPage++;
      }
      return allPresences;
    } catch (err) {
      return [];
    }
  };

  const handleExportPDF = async () => {
    setLoading(true);
    try {
      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.text(`Rapport de présence du jour - ${currentOrganisation?.nom || ''}`, 14, 20);
      const allPresences = await fetchAllPresencesForToday();
      const tableColumn = ["Nom", "Prénom", "Date", "Heure", "Type", "Période", "Statut", "Justifié"];
      const tableRows = allPresences.map((p) => ([
        p.employe_nom, p.employe_prenom, p.date || '-', formatHeure(p.heure),
        p.type_pointage === "entree" ? "Entrée" : "Sortie",
        p.periode === "autre" ? "Exceptionnelle" : p.periode.charAt(0).toUpperCase() + p.periode.slice(1).replace('_', ' '),
        p.statut,
        (p.statut === "RETARD" || p.statut === "ABSENT") ? (p.est_justifiee ? "Oui" : "Non") : "-"
      ]));
      const presentCount = allPresences.filter(p => p.statut === 'PRESENT').length;
      const retardCount = allPresences.filter(p => p.statut === 'RETARD').length;
      const absentCount = allPresences.filter(p => p.statut === 'ABSENT').length;
      doc.setFontSize(12);
      doc.text(`Total: ${allPresences.length} | Présents: ${presentCount} | Retards: ${retardCount} | Absents: ${absentCount}`, 14, 30);
      autoTable(doc, { head: [tableColumn], body: tableRows, startY: 40, styles: { fontSize: 10 }, headStyles: { fillColor: [27, 1, 155] } });
      doc.save(`presence_jour_${new Date().toLocaleDateString().replace(/\//g, '-')}_${currentOrganisation?.nom || ''}.pdf`);
    } catch (err) {
      console.error("Erreur PDF", err);
    } finally {
      setLoading(false);
    }
  };

  // Style partagé pour les boutons Détail 
  const btnDetailStyle = {
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    padding: '2px 8px', fontSize: '0.75rem',
    backgroundColor: '#e9ecef', color: '#495057',
    border: '1px solid #dee2e6', borderRadius: '4px', cursor: 'pointer'
  };

  return (
    <div style={{ ...styles.container, marginLeft: sidebarVisible ? 250 : 0 }}>

      {/* ── Modal justification  ── */}
      {showDetailModal && detailJustif && (
        <div className="modal fade show d-block" tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" style={{ color: '#1B019B' }}>
                  Détail de la Justification de {detailJustif.employe_nom} {detailJustif.employe_prenom}
                </h5>
                <button className="btn-close" onClick={closeDetailModal}></button>
              </div>
              <div className="modal-body">
                <p><strong>Texte :</strong></p>
                <p>{detailJustif.texte || 'Aucun texte fourni'}</p>
                {detailJustif.fichier && (
                  <>
                    <p><strong>Fichier joint :</strong></p>
                    <a href={detailJustif.fichier} target="_blank" rel="noopener noreferrer"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: '#1B019B', textDecoration: 'none', padding: '8px 12px', borderRadius: '6px', backgroundColor: '#f0f0ff', border: '1px solid #d0d0ff' }}>
                      <FaInfoCircle /> Voir le fichier joint
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

{/* ── Modal liste des absents approuvés  ── */}
{showAbsencesListModal && (
  <div 
    className="modal fade show d-block" 
    tabIndex="-1"
    style={{ 
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'block',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1050,
      width: '100vw',
      height: '100vh',
      overflow: 'hidden'
    }}
    onClick={() => setShowAbsencesListModal(false)}
  >
<div
  className="modal-dialog modal-dialog-centered modal-lg"
  style={{
    maxWidth: '900px',
    margin: '1.75rem auto',
    position: 'relative',
    top: '50%',
    transform: `translateY(-50%) translateX(${sidebarVisible ? '80px' : '0px'})`,
    zIndex: 1051
  }}
  onClick={(e) => e.stopPropagation()}
>
      <div className="modal-content" style={{
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        border: '1px solid #dee2e6',
        overflow: 'hidden'
      }}>
        {/* En-tête */}
        <div className="modal-header" style={{
          borderBottom: '1px solid #dee2e6',
          padding: '16px 20px',
          backgroundColor: 'white'
        }}>
          <h5 className="modal-title" style={{ 
            color: '#1B019B',
            fontWeight: '600',
            fontSize: '1.1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <FiAlertTriangle style={{ color: '#856404' }} />
            Absences approuvées — {new Date(selectedDate).toLocaleDateString('fr-FR')}
          </h5>
          <button 
            className="btn-close" 
            onClick={() => setShowAbsencesListModal(false)}
          />
        </div>

        {/* Corps */}
        <div className="modal-body p-0">
          <div style={{
            padding: '12px 20px',
            backgroundColor: '#fff8e1',
            borderBottom: '1px solid #ffc107',
            color: '#856404',
            fontSize: '0.875rem'
          }}>
            <FaInfoCircle style={{ marginRight: '8px' }} />
            Ces employés n'apparaissent pas dans le tableau car leur absence a été approuvée pour cette journée.
          </div>

          <div className="table-responsive" style={{
            maxHeight: '60vh',
            overflowY: 'auto'
          }}>
            <table className="table table-hover m-0" style={{
              fontSize: '0.9rem'
            }}>
              <thead style={{ 
                backgroundColor: '#f8f9fa',
                borderBottom: '2px solid #dee2e6',
                position: 'sticky',
                top: 0,
                zIndex: 2
              }}>
                <tr>
                  <th style={{ padding: '12px 16px', fontWeight: '600' }}>Nom</th>
                  <th style={{ padding: '12px 16px', fontWeight: '600' }}>Prénom</th>
                  <th style={{ padding: '12px 16px', fontWeight: '600' }}>Période</th>
                  <th style={{ padding: '12px 16px', fontWeight: '600' }}>Motif</th>
                  <th style={{ padding: '12px 16px', fontWeight: '600', textAlign: 'center' }}>Détail</th>
                </tr>
              </thead>
              <tbody>
                {absencesApprouvees.employes.map((emp, idx) => (
                  <tr key={idx}>
                    <td style={{ padding: '12px 16px' }}>{emp.nom}</td>
                    <td style={{ padding: '12px 16px' }}>{emp.prenom}</td>
                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                      {new Date(emp.date_debut).toLocaleDateString('fr-FR')} → {new Date(emp.date_fin).toLocaleDateString('fr-FR')}
                    </td>
                    <td style={{ 
                      padding: '12px 16px', 
                      maxWidth: '180px', 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap' 
                    }} title={emp.motif}>
                      {emp.motif || '-'}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <button
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '2px 8px',
                          fontSize: '0.75rem',
                          backgroundColor: '#e9ecef',
                          color: '#495057',
                          border: '1px solid #dee2e6',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dee2e6'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                        onClick={() => {
                          setDetailAbsenceApprouvee(emp);
                          setShowDetailAbsenceModal(true);
                        }}
                      >
                        <FaInfoCircle size={12} /> Détail
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pied de page  */}
      </div>
    </div>
  </div>
)}

{/* ── Modal détail d'une absence approuvée  ── */}
{showDetailAbsenceModal && detailAbsenceApprouvee && (
  <div 
    className="modal fade show d-block" 
    tabIndex="-1" 
    style={{ 
      backgroundColor: 'rgba(0,0,0,0.5)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1060,
      width: '100vw',
      height: '100vh',
      overflow: 'hidden'
    }}
    onClick={closeDetailAbsenceModal}
  >
    <div 
      className="modal-dialog modal-dialog-centered"
      style={{
        position: 'relative',
        top: '50%',
        transform: `translateY(-50%) translateX(${sidebarVisible ? '80px' : '0px'})`,

        zIndex: 1061
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="modal-content">
        <div className="modal-header">
          <h5 className="modal-title" style={{ color: '#1B019B' }}>
            Détail de l'absence de {detailAbsenceApprouvee.prenom} {detailAbsenceApprouvee.nom}
          </h5>
          <button className="btn-close" onClick={closeDetailAbsenceModal}></button>
        </div>
        <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          <div style={{ display: 'grid', gap: '15px' }}>
            <div>
              <strong>Employé:</strong> {detailAbsenceApprouvee.prenom} {detailAbsenceApprouvee.nom}
            </div>
            <div>
              <strong>Date début:</strong> {detailAbsenceApprouvee.date_debut}
            </div>
            <div>
              <strong>Date fin:</strong> {detailAbsenceApprouvee.date_fin}
            </div>
            <div>
              <strong>Statut:</strong> 
              <span className="badge bg-success" style={{ marginLeft: '8px' }}>Approuvée</span>
            </div>
            <div>
              <strong>Motif:</strong>
              <div style={{ 
                marginTop: '5px',
                padding: '8px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                fontSize: '1rem',
                textAlign: 'justify',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                overflowWrap: 'break-word'
              }}>
                {detailAbsenceApprouvee.motif || 'Aucun motif fourni'}
              </div>
            </div>
            {detailAbsenceApprouvee.justificatif && (
              <div>
                <strong>Fichier joint:</strong>
                <div style={{ marginTop: '5px' }}>
                  <a 
                    href={detailAbsenceApprouvee.justificatif} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      gap: '6px',
                      color: '#1B019B',
                      textDecoration: 'none',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      backgroundColor: '#f0f0ff',
                      border: '1px solid #d0d0ff'
                    }}
                  >
                    <FaEye /> Voir le fichier
                  </a>
                </div>
              </div>
            )}
            {detailAbsenceApprouvee.motif_refus && (
              <div>
                <strong>Motif de refus:</strong>
                <div style={{ 
                  marginTop: '5px',
                  padding: '8px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '4px'
                }}>
                  {detailAbsenceApprouvee.motif_refus}
                </div>
              </div>
            )}
          </div>
        </div>
        {/* Pied de page  */}
      </div>
    </div>
  </div>
)}


      {/* ── Modal image agrandie ── */}
      {selectedImage && (
        <div style={styles.modalOverlay} onClick={closeImageModal}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button style={styles.closeButton} onClick={closeImageModal}><FaTimes /></button>
            <img src={selectedImage.src} alt={selectedImage.name} style={styles.modalImage}
              onError={(e) => { e.target.onerror = null; e.target.src = '/default-avatar.png'; }} />
            <div style={styles.employeeName}>{selectedImage.name}</div>
          </div>
        </div>
      )}

      {/* ── Modal motif sortie ── */}
      {motifModal.show && (
        <div className="modal fade show d-block" tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={closeMotifModal}>
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" style={{ color: '#1B019B' }}>
                  Motif de sortie - {motifModal.name}
                </h5>
                <button className="btn-close" onClick={closeMotifModal}></button>
              </div>
              <div className="modal-body">
                <p style={{ fontSize: '1rem', textAlign: 'justify', whiteSpace: 'pre-wrap', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                  {motifModal.motif}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="text-center mb-3">
        <h3 style={styles.header}>Présences du Jour</h3>
        <hr style={styles.hr} />
      </div>

      <div style={styles.organisationHeader}>
        <OrganisationSelector
          onOrganisationChange={handleOrganisationChange}
          selectedOrganisation={selectedOrganisation}
        />
        {currentOrganisation && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: '#666', fontSize: '14px' }}>
              Gestion des présences pour cette organisation
            </span>
          </div>
        )}
      </div>

      {currentOrganisation ? (
        <>
          <h1 style={styles.title}>📋 PRÉSENCES - {currentOrganisation.nom}</h1>

          <div style={styles.filterContainer}>
            <div style={styles.filterGroup}>
              <FaSearch style={styles.filterIcon} />
              <input type="text" placeholder="Rechercher nom, prénom, période..."
                style={styles.searchBar} value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div style={styles.filterGroup}>
              <FaCalendarAlt style={styles.filterIcon} />
              <input type="date" value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)} style={styles.select} />
            </div>
            <div style={styles.filterGroup}>
              <FaClock style={styles.filterIcon} />
              <select style={styles.select} value={periodeFilter} onChange={(e) => setPeriodeFilter(e.target.value)}>
                <option value="">Toutes périodes</option>
                <option value="matin">Matin</option>
                <option value="apres_midi">Après-midi</option>
                <option value="pause">Pause</option>
                <option value="descente">Descente</option>
                <option value="autre">Exceptionnelle</option>
              </select>
            </div>
            <div style={styles.filterGroup}>
              <FiAlertTriangle style={styles.filterIcon} />
              <select style={styles.select} value={statutFilter} onChange={(e) => setStatutFilter(e.target.value)}>
                <option value="">Tous statuts</option>
                <option value="PRESENT">Présent</option>
                <option value="RETARD">Retard</option>
                <option value="ABSENT">Absent</option>
              </select>
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.pageSizeLabel}>Afficher :</label>
              <select style={styles.select} value={pageSize}
                onChange={(e) => setPageSize(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                disabled={loading}>
                {generatePageSizeOptions().map((size, idx) => (
                  <option key={idx} value={size}>
                    {size === 'all' ? 'Tous afficher' : `${size} lignes`}
                  </option>
                ))}
              </select>
            </div>
            <div>
              {(user?.is_super_admin || user?.permissions?.includes("EXPORT_PDF")) && (
                <button onClick={handleExportPDF} disabled={loading} style={styles.exportButton}>
                  <FaDownload /> {loading ? 'Génération du PDF...' : 'Exporter PDF'}
                </button>
              )}
            </div>
          </div>

          {/* ── Bannière présences exceptionnelles ── */}
          {showExceptionnelleBanner && (
            <div style={{ backgroundColor: '#e7f3ff', border: '1px solid #b8daff', borderRadius: '8px', padding: '12px 20px', marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '15px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#004085" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <circle cx="12" cy="16" r="0.5" fill="#004085" stroke="none"/>
                </svg>
                <span style={{ color: '#004085', fontWeight: '500' }}>
                  Des <span style={{ marginLeft: '4px', color: '#28a745' }}>présences exceptionnelles</span> ont été enregistrées pour le {new Date(selectedDate).toLocaleDateString()}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={loadExceptionnellePresences} disabled={loadingExceptionnelle}
                  style={{ backgroundColor: '#004085', color: 'white', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#002752'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#004085'}>
                  {loadingExceptionnelle ? 'Chargement...' : 'Voir ces présences'}
                </button>
                <button onClick={() => setShowExceptionnelleBanner(false)}
                  style={{ backgroundColor: 'transparent', color: '#004085', border: '1px solid #004085', borderRadius: '6px', padding: '8px 16px', fontSize: '14px', cursor: 'pointer' }}>
                  Ignorer
                </button>
              </div>
            </div>
          )}

          {showAbsencesBanner && (
            <div
              style={{
                background: 'linear-gradient(135deg, #fffdf5 0%, #fff8e1 100%)',
                border: '1px solid #ffe082',
                borderLeft: '5px solid #ffb300',
                borderRadius: '12px',
                padding: '16px 20px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '20px',
                boxShadow: '0 3px 10px rgba(0,0,0,0.05)',
              }}
            >
              {/* Partie gauche */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '15px',
                  flex: 1,
                  minWidth: '300px',
                }}
              >
                <div
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    backgroundColor: '#fff3cd',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <FiAlertTriangle
                    style={{
                      color: '#d97706',
                      fontSize: '22px',
                    }}
                  />
                </div>

                <div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      marginBottom: '6px',
                      flexWrap: 'wrap',
                    }}
                  >
                    <span
                      style={{
                        backgroundColor: '#856404',
                        color: '#fff',
                        borderRadius: '20px',
                        padding: '4px 12px',
                        fontSize: '13px',
                        fontWeight: '600',
                      }}
                    >
                      {absencesApprouvees.count} employé
                      {absencesApprouvees.count > 1 ? 's' : ''}
                    </span>

                    <span
                      style={{
                        color: '#5f4b00',
                        fontWeight: '600',
                        fontSize: '15px',
                      }}
                    >
                      Absence approuvée en cours
                    </span>
                  </div>

                  <p
                    style={{
                      margin: 0,
                      color: '#6c5a00',
                      lineHeight: '1.5',
                      fontSize: '14px',
                    }}
                  >
                    {absencesApprouvees.count > 1 ? 'Ces employés sont' : 'Cet employé est'} couverts par une absence approuvée incluant la journée du{' '}
                    <strong>
                      {new Date(selectedDate).toLocaleDateString('fr-FR')}
                    </strong>{' '}
                    et {absencesApprouvees.count > 1 ? 'ne figurent' : 'ne figure'} donc
                    pas dans le tableau des présences.
                  </p>
                </div>
              </div>

              {/* Boutons */}
              <div
                style={{
                  display: 'flex',
                  gap: '10px',
                  flexWrap: 'wrap',
                }}
              >
                <button
                  onClick={() => setShowAbsencesListModal(true)}
                  style={{
                    backgroundColor: '#856404',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px 18px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 2px 6px rgba(133,100,4,0.25)',
                  }}
                >
                  <FaInfoCircle size={14} />
                  Voir la liste
                </button>

                <button
                  onClick={() => setShowAbsencesBanner(false)}
                  style={{
                    backgroundColor: 'white',
                    color: '#856404',
                    border: '1px solid #d6c48a',
                    borderRadius: '8px',
                    padding: '10px 18px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                >
                  Ignorer
                </button>
              </div>
            </div>
          )}

          {/* ── Tableau ── */}
          <div className="card shadow-sm mb-4" style={styles.card}>
            <div className="card-body table-responsive p-0">
              <table className="table table-hover text-center align-middle m-0">
                <thead>
                  <tr style={styles.tableHeader}>
                    <th>Photo</th>
                    <th>Nom</th>
                    <th>Prénom</th>
                    <th>Date</th>
                    <th>Heure</th>
                    <th>Type</th>
                    <th>Période</th>
                    <th>Statut</th>
                    <th>Motif sortie</th>
                    <th>Justifié</th>
                  </tr>
                </thead>
                <tbody>
                  {presences.length ? presences.map((p) => (
                    <tr key={p.id}>
                      <td style={styles.photoCell}>
                        {p.photo ? (
                          <img src={p.photo} alt={`${p.employe_prenom} ${p.employe_nom}`}
                            style={styles.photo}
                            onError={(e) => { e.target.onerror = null; e.target.src = '/default-avatar.png'; }}
                            onClick={() => openImageModal(p)}
                            onMouseEnter={(e) => { e.target.style.transform = 'scale(1.1)'; e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)'; }}
                            onMouseLeave={(e) => { e.target.style.transform = 'scale(1)'; e.target.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)'; }} />
                        ) : (
                          <div style={styles.defaultPhoto}></div>
                        )}
                      </td>
                      <td>{p.employe_nom}</td>
                      <td>{p.employe_prenom}</td>
                      <td>{p.date}</td>
                      <td>{formatHeure(p.heure)}</td>
                      <td>
                        {p.type_pointage === 'entree' ? <><FaSignInAlt style={styles.entryIcon} /> Entrée</>
                          : p.type_pointage === 'sortie' ? <><FaSignOutAlt style={styles.exitIcon} /> Sortie</>
                          : null}
                      </td>
                      <td>
                        {p.periode === "autre"
                          ? <span title={p.motif_sortie || ''} style={styles.exceptionnellePeriod}><FiAlertTriangle /> Exceptionnelle</span>
                          : p.periode === "matin" ? <span style={styles.matinPeriod}><FiSunrise /> Matin</span>
                          : p.periode === "apres_midi" ? <span style={styles.apresMidiPeriod}><FiSunset /> Après-midi</span>
                          : p.periode === "descente" ? <span style={styles.descentePeriod}><FiArrowDownCircle /> Descente</span>
                          : p.periode === "pause" ? <span style={styles.pausePeriod}><FiCoffee /> Pause</span>
                          : <span style={styles.defaultPeriod}>{p.periode?.charAt(0).toUpperCase() + p.periode?.slice(1).replace('_', ' ') || '-'}</span>}
                      </td>
                      <td>
                        <span className={`badge rounded-pill ${p.statut === "PRESENT" ? 'bg-success' : p.statut === "RETARD" ? 'bg-warning' : 'bg-danger'}`}>
                          {p.statut}
                        </span>
                      </td>
                      <td>
                        {p.motif_sortie ? (
                          <button onClick={() => openMotifModal(p)} style={styles.motifButton}>
                            <FaInfoCircle /> Détail
                          </button>
                        ) : '-'}
                      </td>
                      <td>
                        {p.statut === "RETARD" || p.statut === "ABSENT" ? (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                            <span className={`badge rounded-pill ${p.est_justifiee ? 'bg-success' : 'bg-danger'}`}>
                              {p.est_justifiee ? "Oui" : "Non"}
                            </span>
                            {p.est_justifiee && p.justifications && p.justifications.length > 0 && (
                              <button
                                onClick={() => {
                                  setDetailJustif({ ...p.justifications[0], employe_nom: p.employe_nom, employe_prenom: p.employe_prenom, statut: p.statut });
                                  setShowDetailModal(true);
                                }}
                                style={btnDetailStyle}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dee2e6'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}>
                                <FaInfoCircle size={12} /> Détail
                              </button>
                            )}
                          </div>
                        ) : "-"}
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="10" className="text-muted py-4">
                        {loading ? 'Chargement en cours...' : "Aucune présence enregistrée aujourd'hui."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="card-footer d-flex justify-content-center mt-3 bg-transparent border-top-0">
                <nav>
                  <ul className="pagination mb-0">
                    <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => handlePageChange(1)} disabled={loading}
                        style={page === 1 ? styles.paginationDisabled : styles.paginationButton}>Première</button>
                    </li>
                    <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => handlePageChange(page - 1)} disabled={loading}
                        style={page === 1 ? styles.paginationDisabled : styles.paginationButton}>&laquo;</button>
                    </li>
                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      let pageNum;
                      if (pagination.totalPages <= 5) pageNum = i + 1;
                      else if (page <= 3) pageNum = i + 1;
                      else if (page >= pagination.totalPages - 2) pageNum = pagination.totalPages - 4 + i;
                      else pageNum = page - 2 + i;
                      return (
                        <li key={pageNum} className={`page-item ${page === pageNum ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => handlePageChange(pageNum)} disabled={loading}
                            style={page === pageNum ? { ...styles.paginationButton, ...styles.paginationActive } : styles.paginationButton}>
                            {pageNum}
                          </button>
                        </li>
                      );
                    })}
                    <li className={`page-item ${page === pagination.totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => handlePageChange(page + 1)} disabled={loading}
                        style={page === pagination.totalPages ? styles.paginationDisabled : styles.paginationButton}>&raquo;</button>
                    </li>
                    <li className={`page-item ${page === pagination.totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => handlePageChange(pagination.totalPages)} disabled={loading}
                        style={page === pagination.totalPages ? styles.paginationDisabled : styles.paginationButton}>Dernière</button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>

          {(user?.is_super_admin || user?.permissions?.includes("VIEW_PRESENCE_HISTORY")) && (
            <div className="text-end mt-4">
              <Link to="/historique-presences" className="btn shadow-sm" style={styles.historyButton}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#0d0066')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#1B019B')}>
                <FaHistory size={16} /> Voir l'historique des présences
              </Link>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-5">
          <p className="text-muted">Sélectionnez une organisation pour commencer</p>
        </div>
      )}
    </div>
  );
};

export default PresencePage;